import type { OrgMember } from '@sea-snag/shared';
import { createInvitationSchema } from '@sea-snag/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { useState } from 'react';
import { toast } from 'sonner';

import { RequireOwnerOrAdmin } from '@/components/require-owner-or-admin';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { LoadingState } from '../../components/loading-state';
import { useOrganization } from '../../contexts/organization-context';
import { useUser } from '../../hooks/use-auth';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import { invitationsApi, membersApi } from '../../lib/api-client';
import { assignableRoles, isRoleEditable } from '../../lib/member-roles';
import { queryKeys } from '../../lib/query-keys';

export const Route = createFileRoute('/$orgPid/members')({
  component: GuardedMembersPage,
});

/** Members are redirected to the dashboard; the API refuses them too. */
function GuardedMembersPage() {
  return (
    <RequireOwnerOrAdmin>
      <MembersPage />
    </RequireOwnerOrAdmin>
  );
}

function MembersPage() {
  const { organization, isLoading } = useRequiredOrganization();

  if (isLoading) return <LoadingState />;

  return <MembersContent orgPid={organization.pid} />;
}

const ROLE_ORDER: Record<string, number> = { owner: 0, admin: 1, member: 2 };

function roleBadgeVariant(role: string) {
  switch (role) {
    case 'owner':
      return 'outline' as const;
    case 'admin':
      return 'secondary' as const;
    default:
      return 'default' as const;
  }
}

function roleLabel(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function MembersContent({ orgPid }: { orgPid: string }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null);
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { organization } = useOrganization();

  // A super admin is resolved to `owner` on any organization by TenantGuard, so
  // the dropdown has to treat them that way or it would offer them less than
  // the API will accept.
  const viewerRole = user?.isSuperAdmin ? 'owner' : organization?.role;

  const {
    data: members,
    isLoading: membersLoading,
    isError: membersError,
  } = useQuery({
    queryKey: queryKeys.members.list(orgPid),
    queryFn: () => membersApi.list(orgPid),
  });

  const {
    data: invitations,
    isLoading: invitationsLoading,
    isError: invitationsError,
  } = useQuery({
    queryKey: queryKeys.invitations.list(orgPid),
    queryFn: () => invitationsApi.list(orgPid),
  });

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => invitationsApi.revoke(orgPid, invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invitations.list(orgPid) });
      toast.success('Invitation revoked');
    },
    onError: () => {
      toast.error('Failed to revoke invitation');
    },
  });

  const isLoading = membersLoading || invitationsLoading;
  const hasError = membersError || invitationsError;

  const sortedMembers = [...(members ?? [])].sort(
    (a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9)
  );

  const pendingInvitations = (invitations ?? []).filter((inv) => inv.status === 'pending');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Team</h1>
          <p className="text-muted-foreground">Manage your organization members and invitations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>Invite Member</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a new member</DialogTitle>
              <DialogDescription>
                Send an invitation email to add someone to your organization.
              </DialogDescription>
            </DialogHeader>
            <InviteForm
              orgPid={orgPid}
              onSuccess={() => {
                setDialogOpen(false);
                void queryClient.invalidateQueries({
                  queryKey: queryKeys.invitations.list(orgPid),
                });
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>Active members and pending invitations</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : hasError ? (
            <p className="text-destructive">Failed to load team members. Please try again later.</p>
          ) : sortedMembers.length === 0 && pendingInvitations.length === 0 ? (
            <p className="text-muted-foreground">No members yet.</p>
          ) : (
            <div className="space-y-3">
              {sortedMembers.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  orgPid={orgPid}
                  viewerRole={viewerRole}
                  isSelf={member.id === user?.id}
                />
              ))}

              {pendingInvitations.length > 0 && sortedMembers.length > 0 && (
                <div className="border-t pt-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Pending Invitations
                  </p>
                </div>
              )}

              {pendingInvitations.map((inv) => {
                // Revoke endpoint expects the public UUID (pid). Fall back to
                // `id` for any legacy shape, though the schema now exposes pid.
                const invitationPid = inv.pid ?? String(inv.id);
                return (
                  <div
                    key={invitationPid}
                    className="flex items-center justify-between rounded-lg border border-dashed p-4"
                  >
                    <div className="space-y-1">
                      <p className="font-medium text-muted-foreground">{inv.email}</p>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">Pending</Badge>
                        <Badge variant={roleBadgeVariant(inv.role)}>{roleLabel(inv.role)}</Badge>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRevokeTarget({ id: invitationPid, email: inv.email })}
                      disabled={revokeMutation.isPending}
                    >
                      Revoke
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title="Revoke invitation"
        description={`Are you sure you want to revoke the invitation to ${revokeTarget?.email}? They will no longer be able to join using this link.`}
        confirmLabel="Revoke"
        variant="destructive"
        onConfirm={() => {
          if (revokeTarget) {
            revokeMutation.mutate(revokeTarget.id);
            setRevokeTarget(null);
          }
        }}
      />
    </div>
  );
}

function MemberRow({
  member,
  orgPid,
  viewerRole,
  isSelf,
}: {
  member: OrgMember;
  orgPid: string;
  viewerRole: string | undefined;
  isSelf: boolean;
}) {
  const joined = new Date(member.joinedAt).toLocaleDateString('en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const queryClient = useQueryClient();
  const editable = isRoleEditable({ viewerRole, targetRole: member.role, isSelf });
  const options = assignableRoles(viewerRole);

  const updateRole = useMutation({
    mutationFn: (role: string) => membersApi.updateRole(orgPid, member.id, role),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.members.list(orgPid) });
      toast.success(`${member.name} is now ${roleLabel(updated.role).toLowerCase()}`);
    },
    onError: async (err) => {
      // The server owns this decision, so show what it said rather than a
      // guess — "an organization must keep at least one owner" is the useful
      // sentence, not "failed to update".
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        toast.error(body.message || 'Could not change this role');
      } else {
        toast.error('Could not change this role');
      }
    },
  });

  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div className="space-y-1">
        <p className="font-medium">{member.name}</p>
        <p className="text-sm text-muted-foreground">{member.email}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-muted-foreground">{joined}</span>
        {editable ? (
          <Select
            value={member.role}
            disabled={updateRole.isPending}
            onValueChange={(role) => updateRole.mutate(role)}
          >
            <SelectTrigger className="h-8 w-[130px]" aria-label={`Role for ${member.name}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((role) => (
                <SelectItem key={role} value={role}>
                  {roleLabel(role)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge
            variant={roleBadgeVariant(member.role)}
            // Says why there is no control, rather than leaving it looking broken.
            title={
              isSelf
                ? 'You cannot change your own role — another owner can'
                : 'Only an owner can change this role'
            }
          >
            {roleLabel(member.role)}
          </Badge>
        )}
      </div>
    </div>
  );
}

// Boat owners are not invited into accounts any more (#242): they get a magic
// link from the Owners screen, which needs no password and no membership row.
type InviteRoleValue = 'admin' | 'member';

function InviteForm({ orgPid, onSuccess }: { orgPid: string; onSuccess: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRoleValue>('member');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const parsed = createInvitationSchema.safeParse({ email, role });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid form input');
      return;
    }

    setSubmitting(true);
    try {
      await invitationsApi.create(orgPid, parsed.data);
      toast.success(`Invitation sent to ${email}`);
      onSuccess();
    } catch (err) {
      if (err instanceof HTTPError) {
        const body = (await err.response.json().catch(() => ({}))) as { message?: string };
        setError(body.message || 'Failed to send invitation');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4" aria-label="Invite member form">
      {error && (
        <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="invite-email">Email address</Label>
        <Input
          id="invite-email"
          type="email"
          placeholder="colleague@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="invite-role">Role</Label>
        <Select value={role} onValueChange={(v: string) => setRole(v as InviteRoleValue)}>
          <SelectTrigger id="invite-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="member">Member</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Boat owners are not invited here — add them on the Owners screen, which sends them a link
          to their own page.
        </p>
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Sending...' : 'Send Invitation'}
      </Button>
    </form>
  );
}
